import React from "react";
import { useNavigate } from "react-router-dom";
import type { AdminTabId } from "../AdminWorkspace/adminTabs";
import { RequestItem } from "../../api/requests";
import { RequestTablePagination } from "./RequestTablePagination";
import { formatScheduleDateTime, getNearestNextRunAt } from "../../utils/nearestScheduleRun";
import { formatTaskSchedulePeriodicity } from "../../utils/formatSchedulePeriodicity";
import "./RequestTable.scss";

interface RequestTableProps {
  data: RequestItem[];
  page: number;
  pageSize: number;
  returnAdminTab?: AdminTabId;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const getStatusLabel = (status: string): string => {
  switch (status) {
    case "error":
      return "Ошибка";
    case "new":
      return "Новая";
    case "scheduled":
      return "Запланирована";
    case "processing":
    case "in_progress":
      return "В обработке";
    case "finished":
      return "Завершена";
    case "done":
      return "Готово";
    default:
      return status;
  }
};

export const RequestTable: React.FC<RequestTableProps> = ({
  data,
  page,
  pageSize,
  returnAdminTab,
  onPageChange,
  onPageSizeChange,
}) => {
  const navigate = useNavigate();
  const hasNextPage = data.length >= pageSize;
  const totalPages = hasNextPage ? page + 2 : page + 1;
  const visibleData = data;

  return (
    <div className="request-table-wrapper">
      <div className="request-table-card">
        <table className="request-table">
          <thead>
            <tr>
              <th>Компания</th>
              <th>ИНН</th>
              <th>URL</th>
              <th>Запланированная проверка</th>
              <th>Статус</th>
            </tr>
          </thead>
          <tbody>
            {visibleData.length === 0 ? (
              <tr>
                <td colSpan={5} className="request-table-empty">
                  Нет данных
                </td>
              </tr>
            ) : (
              visibleData.map((item) => {
                const nearestRun = getNearestNextRunAt(item.schedule);
                const schedulePeriodicity = formatTaskSchedulePeriodicity(item.schedule);
                return (
                  <tr
                    key={item.id}
                    className="request-table-row-clickable"
                    onClick={() =>
                      navigate(
                        `/request/${item.id}`,
                        returnAdminTab ? { state: { adminTab: returnAdminTab } } : undefined
                      )
                    }
                  >
                    <td>{item.companyName}</td>
                    <td>{item.inn}</td>
                    <td>
                      <div className="request-table-urls">
                        <span className="request-table-link">{item.url}</span>
                      </div>
                    </td>
                    <td>
                      {schedulePeriodicity || nearestRun ? (
                        <div className="request-table-scheduled-cell">
                          {schedulePeriodicity ? (
                            <span className="request-table-schedule-period">
                              {schedulePeriodicity}
                            </span>
                          ) : null}
                          {nearestRun ? (
                            <span className="request-table-scheduled">
                              {formatScheduleDateTime(nearestRun)}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="request-table-scheduled request-table-scheduled--empty">
                          —
                        </span>
                      )}
                    </td>
                    <td>
                      {item?.status && (
                        <span className="request-table-status">{getStatusLabel(item.status)}</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <RequestTablePagination
        page={page}
        totalPages={totalPages}
        pageSize={pageSize}
        canGoNext={hasNextPage}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
};
